/**
 * # Google Analytics integration
 *
 * Track page/screen views and custom events for analytics and error reporting.
 *
 * This is an adaptation of the new Google Analytics tracking code.
 */

import debounce from "lodash/debounce";
import { transform } from "objectro";
import config from "../config";
import { AppServiceGoogleAnalytics, AppConfig } from "../types";
import appEvents from "./appEvents";
import debug from "./debug";
import { get, hasAndIsNotEmpty } from "./helpers";

interface GoogleAnalyticsFunction extends Function {
  q: any[];
  l: number;
}

declare global {
  interface Window {
    ga: GoogleAnalyticsFunction;
  }
}

// Google Analytics says we can send only a maximum of 10 events
// per second (1000ms). This pending queue array will be where we
// first push events to, which will then be sent to GA's queue.
// Batches of 10 events are sent to GA's queue immediately, then
// after every 1 second if the number of pending events exceeds
// the max quota.
const MAX_QUOTA = 10;
const PENDING_DELAY = 1000;
export const PENDING_QUEUE: any[] = [];
let pendingTimer: any = 0;
let processingPending = false;

// This is the GA queue array that will store the GA events that
// the GA analytics.js script then publishes to the service.
export const QUEUE: any[] = [];

// Google Analytics cached events object
window.ga =
  window.ga ||
  function() {
    (window.ga.q = window.ga.q || QUEUE).push(arguments);
  };
window.ga.l = +new Date();

/**
 * Take the oldest events pushed to the pending queue
 * and push them via `window.ga` so they can be sent.
 */
export const processPendingQueue = debounce(() => {
  debug("[googleAnalytics] processPendingQueue", {
    PENDING_QUEUE,
    processingPending
  });

  // If already processing, defer until available
  if (processingPending) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    deferProcessPendingQueue();
  } else {
    if (PENDING_QUEUE.length) {
      processingPending = true;

      const batchedEvents = PENDING_QUEUE.splice(0, MAX_QUOTA);
      batchedEvents.forEach(queuedEvent => {
        window.ga(...queuedEvent);
      });

      debug(`[googleAnalytics] processed ${batchedEvents.length} events`, {
        batchedEvents,
        PENDING_QUEUE
      });

      processingPending = false;

      // If more items are pending, process them again after the delay
      if (PENDING_QUEUE.length) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        deferProcessPendingQueue();
      }
    }
  }
}, 50);

/**
 * Process the pending queue after a timer has run.
 */
export function deferProcessPendingQueue() {
  debug("[googleAnalytics] deferProcessPendingQueue", {
    PENDING_QUEUE,
    processingPending
  });

  clearTimeout(pendingTimer);
  pendingTimer = setTimeout(processPendingQueue, PENDING_DELAY);
}

/**
 * Custom dimensions and metrics mapper.
 *
 * This will take an event's extra options object and map the human-readable
 * app's custom dimension/metric keys and map accordingly outputting a
 * an object compatible with sending via Google Analytics.
 */
export function mapCustomMetricsAndDimensions(
  config: AppConfig,
  { dimensions, metrics, ...data }: any
) {
  let output = { ...data };
  if (
    dimensions &&
    hasAndIsNotEmpty(config, "services.googleAnalytics.customise.dimensions")
  ) {
    output = {
      ...output,
      ...transform(
        dimensions,
        config.services.googleAnalytics.customise.dimensions
      )
    };
  }

  if (
    metrics &&
    hasAndIsNotEmpty(config, "services.googleAnalytics.customise.metrics")
  ) {
    output = {
      ...output,
      ...transform(metrics, config.services.googleAnalytics.customise.metrics)
    };
  }
  return output;
}

/**
 * Publish an event to google analytics
 */
export function push(...event: any[]): void {
  PENDING_QUEUE.push(event);
  processPendingQueue();
}

/**
 * Initialise Google Analytics by injecting the script into the head of the document.
 */
export function initGoogleAnalytics({
  ua,
  options = {}
}: AppServiceGoogleAnalytics): void {
  // Ignore for testing
  debug("[googleAnalytics] initGoogleAnalytics:", ua);
  if (process.env.NODE_ENV === "test") {
    return;
  }

  // Only inject it if it hasn't been done already
  let findGAScript:
    | NodeListOf<HTMLScriptElement>
    | undefined = document.querySelectorAll("head script");
  let hasGAScript: boolean | number =
    findGAScript.length &&
    Array.from(findGAScript).filter(elem => /google-analytics/.test(elem.src))
      .length > 0;

  // Load in GA
  if (!hasGAScript) {
    const head: HTMLHeadElement = document.querySelector(
      "head"
    ) as HTMLHeadElement;
    const gaScript = document.createElement("script");
    gaScript.async = true;
    gaScript.src = "https://www.google-analytics.com/analytics.js";
    head.appendChild(gaScript);
    push("create", ua, "auto", undefined, options);
    push("send", "pageview");
    push((tracker: any) => {
      debug(
        `[googleAnalytics] Google Analytics has been loaded and initialised from ${gaScript.src}`,
        { tracker }
      );

      /**
       * Trigger the `analytics.initialised` event when the tracker is ready.
       */
      appEvents.trigger("analytics.initialised", {
        ua,
        options,
        tracker
      });
    });

    debug("[googleAnalytics] Loading Google Analytics...");
  } else {
    debug("[googleAnalytics] Google Analytics script already exists!");
  }
}

/**
 * Track a user.
 */
export function trackUser({ id }: { id: string | number }): void {
  push("set", "userId", id);
}

/**
 * Track a page view.
 */
export function trackPageView(path: string): void {
  push("send", {
    hitType: "pageview",
    path
  });
}

/**
 * Track a screen view.
 */
export function trackScreenView(title: string): void {
  push("send", {
    hitType: "screenview",
    title
  });
}

/**
 * Track a single event.
 */
export function trackEvent({
  category,
  action,
  label,
  value,
  ...options
}: {
  category: string;
  action: string;
  label: string;
  value?: string | number;
  [optionKey: string]: any;
}): void {
  push("send", {
    hitType: "event",
    eventCategory: category,
    eventAction: action,
    eventLabel: label,
    eventValue: value,
    ...mapCustomMetricsAndDimensions(config, options)
  });
}

/**
 * Track an user timing.
 */
export function trackTiming({
  category,
  action,
  label,
  value,
  ...options
}: {
  category: string;
  action: string;
  label: string;
  value?: string | number;
  [optionKey: string]: any;
}): void {
  push("send", {
    hitType: "timing",
    timingCategory: category,
    timingVar: action,
    timingLabel: label,
    timingValue: value,
    ...mapCustomMetricsAndDimensions(config, options)
  });
}

/**
 * Track an error within the app.
 */
export function trackError({
  category = "error",
  action,
  label,
  value,
  fatal = false,
  ...options
}: {
  category?: string;
  action?: string;
  label: string | Error;
  value?: number;
  fatal?: boolean;
  [optionKey: string]: any;
}): void {
  push("send", {
    hitType: "event",
    eventCategory: category,
    eventAction: `${fatal ? "fatal_error" : "non_fatal_error"}${
      action ? `_${action}` : ""
    }`,
    eventLabel: label,
    eventValue: value,
    nonInteraction: true,
    ...mapCustomMetricsAndDimensions(config, options)
  });
}

/**
 * Connect Google Analytics to the app events
 */
export function bootstrap(partialConfig: AppConfig) {
  appEvents.on("app.initialised", () => {
    if (hasAndIsNotEmpty(config, "services.googleAnalytics.ua")) {
      initGoogleAnalytics({
        ua: get(config, "services.googleAnalytics.ua"),
        options: get(config, "services.googleAnalytics.options")
      });
      push("set", "appId", config.id);
      push("set", "appName", `${config.brand} ${config.name}`);
      push(
        "set",
        "appVersion",
        `${process.env.REACT_APP_VERSION} ${process.env
          .REACT_APP_VERSION_COMMIT || "localdev"}`
      );
    }
  });
  appEvents.on("analytics.user", data => trackUser(data));
  appEvents.on("analytics.pageView", data => trackPageView(data));
  appEvents.on("analytics.screenView", data => trackScreenView(data));
  appEvents.on("analytics.event", data => trackEvent(data));
  appEvents.on("analytics.timing", data => trackTiming(data));
  appEvents.on("analytics.error", data => trackError(data));
}
